import React, { useEffect, useState } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { Theme } from "@mui/material/styles";
import AppContainer from "./external/ensolvers-core-frontend-mui/components/Main/AppContainer";
import SnackbarComponent from "./external/ensolvers-core-frontend-mui/components/Main/SnackbarComponent";
import { LocalizationProvider } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import authService, { AuthEvents, LoginError } from "./external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import AppNavigation from "./components/AppNavigation";
import { STATIC_ICONS, getLandingPageByUser, getSections } from "./utils/NavigationUtils";
import User, { UserEvents } from "./external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/User";
import NotificationService, {
  NotificationEvents,
} from "./external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import { SnackBarData } from "./external/ensolvers-core-frontend-mui/types/SnackbarData";
import { sendSnackBarMessage } from "./utils/SnackBarUtils";
import featureToggleList from "./utils/toggleConfig.json";
import { useFeatureToggle } from "./external/ensolvers-core-frontend-mui/hooks/useFeatureToggle";
import { Features } from "./utils/Features";
import OpsUserService from "./services/OpsUserService";
import AuthService from "./external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import { OpsThemeProvider } from "./theme";
import { STORAGE_KEYS } from "./hooks/useLocalStorage";
import useUserInfo from "./hooks/useUserInfo";
import { ShellTopBar } from "./components/Shell/ShellTopBar";
import { ShellDrawer } from "./components/Shell/ShellDrawer";
import { Token } from "./theme";
import { UserInfoContext } from "./context/UserInfoContext";
import { clearQueryClientOnLogout } from "./queryClient";

export const ROWS_PER_PAGE = 50;
export const MOBILE_WIDTH = 600;

function App(): JSX.Element {
  const userContextValue = useUserInfo();
  const [isLogged, setIsLogged] = useState<boolean>(false);
  const [landingPage, setLandingPage] = useState<string | undefined>(undefined);
  const [isDashboardFeatureEnabled] = useFeatureToggle(featureToggleList, Features.DASHBOARD_FEATURE);
  const [isVacationReportEnabled] = useFeatureToggle(featureToggleList, Features.VACATION_REPORT);
  const [isAccomplishmentEnabled] = useFeatureToggle(featureToggleList, Features.ACCOMPLISHMENTS);
  const [isCertificationsEnabled] = useFeatureToggle(featureToggleList, Features.CERTIFICATIONS);

  useEffect(() => {
    authService.redirectUserIfNeededDependingOnLoginState();

    const error = authService.loginErrorJustHappened();

    if (error === LoginError.NOT_AUTHORIZED) {
      alert("You are not authorized");
    }

    const onLogout = () => {
      localStorage.removeItem(STORAGE_KEYS.STORED_ASSIGNATION);
      localStorage.removeItem(STORAGE_KEYS.STORED_PROJECT);
      localStorage.removeItem(STORAGE_KEYS.STORED_DATE);
      localStorage.removeItem(STORAGE_KEYS.INVOICE_DRAFT);
      localStorage.removeItem(STORAGE_KEYS.LAST_INVOICE_NUMBER);
      clearQueryClientOnLogout();
      setIsLogged(false);
    };

    const onNotificationError = (message: string) => {
      const snackData: SnackBarData = { text: message, severity: "error" };
      sendSnackBarMessage(snackData);
    };

    const onNotificationInfo = (message: string) => {
      const snackData: SnackBarData = { text: message, severity: "success" };
      sendSnackBarMessage(snackData);
    };

    const onNotificationWarning = (message: string) => {
      const snackData: SnackBarData = { text: message, severity: "warning" };
      sendSnackBarMessage(snackData);
    };

    const onOpsUserUpdated = (updated: User) => {
      const currentUser = AuthService.getUser();
      const opsUser = updated as User & { coreUserExternalId?: string };
      const isSelf =
        !!currentUser &&
        (currentUser.externalId === opsUser.coreUserExternalId || (!!opsUser.email && currentUser.email === opsUser.email));
      if (!currentUser || !isSelf) return;
      currentUser.avatarUrl = opsUser.avatarUrl;
      currentUser.email = opsUser.email;
      currentUser.firstName = opsUser.firstName;
      currentUser.lastName = opsUser.lastName;
      currentUser.phone = opsUser.phone;
      AuthService.storeUser(currentUser);
      userContextValue.updateLoggedUser(currentUser);
    };

    authService.subscribe(AuthEvents.LOGOUT, onLogout);
    NotificationService.subscribe(NotificationEvents.ERROR, onNotificationError);
    NotificationService.subscribe(NotificationEvents.INFO, onNotificationInfo);
    NotificationService.subscribe(NotificationEvents.WARNING, onNotificationWarning);
    OpsUserService.subscribe(UserEvents.UPDATE, onOpsUserUpdated);

    return () => {
      authService.unsubscribe(AuthEvents.LOGOUT, onLogout);
      NotificationService.unsubscribe(NotificationEvents.ERROR, onNotificationError);
      NotificationService.unsubscribe(NotificationEvents.INFO, onNotificationInfo);
      NotificationService.unsubscribe(NotificationEvents.WARNING, onNotificationWarning);
      OpsUserService.unsubscribe(UserEvents.UPDATE, onOpsUserUpdated);
    };
  }, []);

  useEffect(() => {
    setIsLogged(authService.isLogged());

    userContextValue.updateLoggedUser(authService.getUser());
  }, [isLogged]);

  useEffect(() => {
    if (userContextValue.user?.externalId) {
      setLandingPage(getLandingPageByUser(userContextValue.user));
    }
  }, [userContextValue.user]);

  return (
    <Router>
      <OpsThemeProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <UserInfoContext.Provider value={userContextValue}>
            <AppContainer
              key={userContextValue.user?.externalId}
              isLogged={isLogged}
              staticIconsActions={STATIC_ICONS}
              customSections={
                userContextValue.user?.externalId
                  ? getSections(
                      userContextValue.user,
                      isDashboardFeatureEnabled,
                      isVacationReportEnabled,
                      isAccomplishmentEnabled,
                      isCertificationsEnabled,
                    )
                  : undefined
              }
              backgroundColor={(theme: Theme) => theme.palette.background.default}
              mainStyle={{ height: "auto", minHeight: 0 }}
              barName={""}
              responsive
              containerSize={"xl"}
              openDrawer
              showAvatarInAppDrawer
              accordionSx={{ whiteSpace: "pre-wrap", textIndent: 4 }}
              stylesForActiveRoute={{
                color: Token.brandAccent,
              }}
              CustomTopBarO={ShellTopBar}
              CustomDrawerO={ShellDrawer}
            >
              <AppNavigation isLogged={isLogged} landingPage={landingPage} />
              <SnackbarComponent />
            </AppContainer>
          </UserInfoContext.Provider>
        </LocalizationProvider>
      </OpsThemeProvider>
    </Router>
  );
}

export default App;
